/**
 * 검색창 매칭 규칙 — 띄어쓰기·기호를 무시하고 찾는다 (2026-09-24)
 *
 * 대표 제보: "더팰리스트데시앙 검색시 현장명이 검색안된다. 다른 현장도 똑같다."
 *   DB 현장명은 '신천동 더 팰리스트 데시앙' 인데 사용자는 붙여서 친다.
 *   기존 지도·목록 검색은 `title.toLowerCase().includes(q)` 라서 공백이 정확히 맞아야만 찾혔다.
 *
 * 규칙
 *  - 공백(전각 공백 포함)과 흔한 구분기호(-·.,()/)를 양쪽 모두에서 지우고 비교한다.
 *  - 대소문자 무시(영문 브랜드: SK뷰, e편한세상).
 *  - 동의어는 백엔드 SEARCH_SYNONYMS 와 같은 뜻을 유지한다(e편한세상 ↔ 이편한세상 등).
 *  - 매칭 대상: 현장명 · 주소 · 시행사 · 시공사 · 브랜드.
 */

/** 비교 전 정규화 — 소문자화 + 공백/구분기호 제거 */
export function normalizeSearch(value: string | null | undefined): string {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/[\s 　]+/g, '')
    .replace(/[-–—·・.,()[\]{}/\\_'"]+/g, '');
}

/**
 * 동의어 — 한쪽으로 통일해서 비교한다(정규화 뒤 형태로 적는다).
 * 백엔드 `api/views.py` SEARCH_SYNONYMS 와 뜻이 어긋나지 않게 유지할 것.
 */
const SYNONYM_CANONICAL: Array<[RegExp, string]> = [
  [/이편한세상/g, 'e편한세상'],
  [/대명힐스테이트2차|힐스테이트대명2차|대명힐스2차|대명센트럴2차/g, '힐스테이트대명센트럴2차'],
];

const canonicalize = (normalized: string): string =>
  SYNONYM_CANONICAL.reduce((acc, [pattern, to]) => acc.replace(pattern, to), normalized);

/** 검색어를 비교 가능한 형태로 — 정규화 + 동의어 통일 */
export function searchKey(value: string | null | undefined): string {
  return canonicalize(normalizeSearch(value));
}

/** 현장 하나가 검색어에 걸리는지. 검색어가 비면 true(필터 미적용). */
export function matchesSearch(
  property: {
    title?: string | null;
    address?: string | null;
    developer?: string | null;
    brand?: string | null;
  },
  query: string,
): boolean {
  const q = searchKey(query);
  if (!q) return true;
  // ⚠️ `constructor`(시공사)는 타입에 선언하지 않는다 — 객체 기본 프로퍼티와 이름이 겹쳐
  //    `constructor?: string` 으로 선언하면 평범한 객체조차 인자로 못 넘긴다(TS2345).
  //    값이 없으면 Object 함수가 잡히므로 문자열일 때만 쓴다.
  const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
  return [
    asText(property.title),
    asText(property.address),
    asText(property.developer),
    asText((property as { constructor?: unknown }).constructor),
    asText(property.brand),
  ].some((field) => field !== '' && searchKey(field).includes(q));
}

/**
 * 분양완료 현장은 카드·묶음 숫자·기본 목록에 넣지 않는다(2026-09-26 대표: 긴박감).
 * 단 이름으로 직접 검색하면 찾아 준다(2026-10-01 대표 승인 — '대명자이 검색 안 됨').
 * 진행 중 결과 뒤에 붙이고, 이미 있는 id 는 다시 넣지 않는다.
 */
export function appendSoldOutMatches<T extends { id: number | string }>(
  base: T[],
  soldOut: Array<T & { title?: string | null; address?: string | null; developer?: string | null; brand?: string | null }>,
  query: string,
  suggestedIds?: number[] | null,
): T[] {
  if (!searchKey(query) || soldOut.length === 0) return base;
  const have = new Set(base.map((p) => String(p.id)));
  const suggested = new Set(suggestedIds ?? []);
  const extra = soldOut.filter(
    (p) => !have.has(String(p.id)) && (suggested.has(Number(p.id)) || matchesSearch(p, query)),
  );
  return extra.length ? [...base, ...extra] : base;
}
